const RAMP_STOPS = {
  10: ['light', 0.95],
  25: ['light', 0.9],
  50: ['light', 0.85],
  100: ['light', 0.72],
  200: ['light', 0.55],
  300: ['light', 0.38],
  400: ['light', 0.18],
  500: ['base', 0],
  600: ['dark', 0.15],
  700: ['dark', 0.3],
  800: ['dark', 0.42],
  900: ['dark', 0.54],
  950: ['dark', 0.68]
};

const DARK_TEXT = '#181D27';
const LIGHT_TEXT = '#FFFFFF';

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

export const hexToRgb = (hex) => {
  if (typeof hex !== 'string') return null;
  let value = hex.trim().replace(/^#/, '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
};

export const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`;

export const isValidHex = (hex) => hexToRgb(hex) !== null;

const mixChannel = (channel, target, amount) => channel * (1 - amount) + target * amount;

const mixColor = (rgb, target, amount) => ({
  r: mixChannel(rgb.r, target, amount),
  g: mixChannel(rgb.g, target, amount),
  b: mixChannel(rgb.b, target, amount)
});

export const generateColorRamp = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return Object.entries(RAMP_STOPS).reduce((ramp, [stop, [direction, amount]]) => {
    if (direction === 'base') ramp[stop] = rgbToHex(rgb);
    else if (direction === 'light') ramp[stop] = rgbToHex(mixColor(rgb, 255, amount));
    else ramp[stop] = rgbToHex(mixColor(rgb, 0, amount));
    return ramp;
  }, {});
};

const channelLuminance = (channel) => {
  const ratio = channel / 255;
  return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
};

export const getReadableTextColor = (hex) =>
  relativeLuminance(hex) > 0.55 ? DARK_TEXT : LIGHT_TEXT;

export const buildBrandingVars = (branding) => {
  const vars = {};
  const primaryRamp = generateColorRamp(branding?.primary_color);
  if (primaryRamp) {
    Object.entries(primaryRamp).forEach(([stop, color]) => {
      vars[`--color-primary-${stop}`] = color;
    });
    vars['--color-on-primary'] = getReadableTextColor(branding.primary_color);
  }
  const secondaryRamp = generateColorRamp(branding?.secondary_color);
  if (secondaryRamp) {
    Object.entries(secondaryRamp).forEach(([stop, color]) => {
      vars[`--color-secondary-${stop}`] = color;
    });
  }
  return vars;
};

export const BRANDING_VAR_KEYS = [
  ...Object.keys(RAMP_STOPS).map((stop) => `--color-primary-${stop}`),
  ...Object.keys(RAMP_STOPS).map((stop) => `--color-secondary-${stop}`),
  '--color-on-primary'
];
